import { defineStore } from "pinia";

import config from "@/config";

import type { UserData } from "@/models/user";
import { jsonStorage } from "@/utils";

import * as Mutations from "./mutations";

import type { UserState } from "./types";
import type { CookieAcknowledgement } from "./types";

const COOKIE_VERSION = 1;

export default defineStore("user", {
    state: (): UserState => ({
        cookieAck: jsonStorage.read<CookieAcknowledgement>("user:cookieAck"),

        persist: jsonStorage.read<boolean>("user:persist", false),
        token: jsonStorage.retrieve<string>("user:token"),

        username: undefined,
        email: undefined,
        profilePicture: undefined
    }),

    getters: {
        hasAcceptedCookies(): boolean | undefined
        {
            if (this.cookieAck?.version === COOKIE_VERSION) { return this.cookieAck.value; }

            return undefined;
        },
        isLogged(): boolean { return !!(this.token); }
    },
    actions: {
        _setCookieAck(value: boolean): void
        {
            this.cookieAck = { value: value, version: COOKIE_VERSION };

            jsonStorage.write("user:cookieAck", this.cookieAck);
        },
        _setInfo(user?: Partial<UserData>): void
        {
            this.username = user?.username;
            this.email = user?.email;
            this.profilePicture = user?.profilePicture;
        },

        _clearToken(): void
        {
            this.token = undefined;

            jsonStorage.clear("user:token");
        },
        _setToken(token?: string, persistent = true): void
        {
            this._clearToken();

            this.persist = persistent;
            this.token = token;

            jsonStorage.write("user:persist", persistent);
            jsonStorage.set("user:token", this.token, persistent);
        },

        clear(): void
        {
            this._setInfo();
            this._clearToken();
        },

        acceptCookies(): void { this._setCookieAck(true); },
        declineCookies(): void { this._setCookieAck(false); },

        async logIn(username: string, password: string, persistent = true): Promise<void>
        {
            const request = new Mutations.Authenticate({ username, password });
            const response = await request.execute();
            const user = response.user;

            this._setToken(response.token, persistent);
            this._setInfo({
                ...user,

                profilePicture: user.profilePicture ? `${config.backendUrl}${user.profilePicture}` : undefined
            });
        },
        async renewToken(): Promise<void>
        {
            const request = new Mutations.RenewSession(this.token!);
            const response = await request.execute();
            const user = response.user;

            this._setToken(response.token, this.persist);
            this._setInfo({
                ...user,

                profilePicture: user.profilePicture ? `${config.backendUrl}${user.profilePicture}` : undefined
            });
        },
        logOut(): Promise<void>
        {
            const request = new Mutations.Disconnect(this.token!);

            this.clear();

            return request.execute();
        }
    }
});
